<template>
    <div class="panel panel-default panel-dashboard">
        <div class="panel-heading">
            <h5 class="pull-left">
                Change Development
            </h5>

            <button type="button" class="btn btn-black btn-xs pull-right" @click="expanded = !expanded"
                    style="margin-top: 5px">
                <i class="fa fa-search"></i> {{ expanded ? 'Collapse' : 'Expand' }}
            </button>

            <div class="clearfix"></div>
        </div>

        <loading-spinner v-if="loading" :style="loadingSpinnerStyle"></loading-spinner>

        <div v-else class="panel-body">
            <monthly-chart
                :year="year"
                :metric-type="metricType"
                :rows="rows"
                :filters="filters"
                :loading="loading"
            ></monthly-chart>

            <hr v-if="expanded">

            <monthly-table v-if="expanded" :year="year" :metric-type="metricType" :rows="rows"></monthly-table>
        </div>
    </div>
</template>

<script>
import http from '@/http'
import moment from 'moment'
import Chart from 'chart.js'
import SaveHeight from '@/mixins/SaveHeight'
import LoadingSpinner from '@/components/Layout/LoadingSpinner'
import MonthlyChart from '@/pages/Dashboards/Subscriptions/Panels/ChangeDevelopment/MonthlyChart'
import MonthlyTable from '@/pages/Dashboards/Subscriptions/Panels/ChangeDevelopment/MonthlyTable'
import SelectWidget from '@/vendor/forms/FormGroups/Widgets/Select'

export default {
    components: {LoadingSpinner, MonthlyChart, MonthlyTable},

    mixins: [SaveHeight],

    props: ['year', 'metricType', 'portal', 'user', 'subscriberType'],

    data() {
        return {
            loading: true,
            expanded: localStorage.getItem('dashboards/subscriptions/change-development/expanded') === 'true',
            rows: null
        }
    },

    mounted() {
        this.load()
    },

    watch: {
        year() {
            this.load()
        },

        metricType() {
            this.load()
        },

        portal() {
            this.load()
        },

        user() {
            this.load()
        },

        subscriberType() {
            this.load()
        },

        expanded(value) {
            localStorage.setItem('dashboards/subscriptions/change-development/expanded', value)
        }
    },

    computed: {
        filters() {
            let filters = []

            if (this.portal && this.portal.length) {
                filters.push({dimension: 'portal_id', operator: 'in', value: this.portal})
            }

            if (this.user) {
                filters.push({dimension: 'user_id', value: this.user})
            }

            if (this.subscriberType) {
                filters.push({dimension: 'subscriber_type', value: this.subscriberType})
            }

            return filters
        },

        startingMetric() {
            return 'starting_subscription_' + this.metricType
        },

        endingMetric() {
            return 'ending_subscription_' + this.metricType
        },

        months() {
            let startDate = moment().year(this.year).startOf('year')

            let endDate = moment().year(this.year).endOf('year')

            let result = []

            while (startDate.isBefore(endDate)) {
                result.push(startDate.clone())

                startDate.add(1, 'month')
            }

            return result
        }
    },

    methods: {
        load() {
            this.saveHeight()

            this.loading = true

            Promise
                .all([
                    this.getActualNewRowsPromise(),
                    this.getExpectedNewRowsPromise(),
                    this.getEndingWithRenewalRowsPromise(),
                    this.getEndingWithoutRenewalRowsPromise(),
                    this.getRenewedRowsPromise()
                ])
                .then(responses => {
                    this.rows = this.months.map(month => this.getRow(responses, month))

                    this.loading = false
                })
        },

        getActualNewRowsPromise() {
            return http.post('/api/v1/reports/dashboard', {
                start: moment().year(this.year).startOf('year').format('YYYY-MM-DD'),
                end: moment().year(this.year).endOf('year').format('YYYY-MM-DD'),
                metrics: ['actual_' + this.startingMetric],
                dimensions: ['month'],
                filters: this.filters.concat([{dimension: 'is_first_subscription_period', value: true}]),
                foo: 'bar'
            })
        },

        getExpectedNewRowsPromise() {
            return http.post('/api/v1/reports/dashboard', {
                start: moment().year(this.year).startOf('year').format('YYYY-MM-DD'),
                end: moment().year(this.year).endOf('year').format('YYYY-MM-DD'),
                metrics: ['expected_' + this.startingMetric],
                dimensions: ['month'],
                filters: this.filters.concat([{dimension: 'is_first_subscription_period', value: true}]),
                foo: 'bar'
            })
        },

        getEndingWithRenewalRowsPromise() {
            return http.post('/api/v1/reports/dashboard', {
                start: moment().year(this.year).startOf('year').format('YYYY-MM-DD'),
                end: moment().year(this.year).endOf('year').format('YYYY-MM-DD'),
                metrics: [this.endingMetric],
                dimensions: ['month'],
                filters: this.filters.concat([{dimension: 'has_automatic_renewal', value: true}]),
                foo: 'bar'
            })
        },

        getEndingWithoutRenewalRowsPromise() {
            return http.post('/api/v1/reports/dashboard', {
                start: moment().year(this.year).startOf('year').format('YYYY-MM-DD'),
                end: moment().year(this.year).endOf('year').format('YYYY-MM-DD'),
                metrics: [this.endingMetric],
                dimensions: ['month'],
                filters: this.filters.concat([{dimension: 'has_automatic_renewal', value: false}]),
                foo: 'bar'
            })
        },

        getRenewedRowsPromise() {
            return http.post('/api/v1/reports/dashboard', {
                start: moment().year(this.year).startOf('year').format('YYYY-MM-DD'),
                end: moment().year(this.year).endOf('year').format('YYYY-MM-DD'),
                metrics: [this.startingMetric],
                dimensions: ['month'],
                filters: this.filters.concat([{dimension: 'is_first_subscription_period', value: false}]),
                foo: 'bar'
            })
        },

        getRow(responses, month) {
            let row = {
                month: month,
                actual_new: this.getValueFromRow(responses[0], month, 'actual_' + this.startingMetric),
                expected_new: this.getValueFromRow(responses[1], month, 'expected_' + this.startingMetric),
                ending_with_renewal: -this.getValueFromRow(responses[2], month, this.endingMetric),
                ending_without_renewal: -this.getValueFromRow(responses[3], month, this.endingMetric),
                renewed: this.getValueFromRow(responses[4], month, this.startingMetric)
            }

            row.new = row.actual_new + row.expected_new

            row.net_change = row.actual_new + row.expected_new + row.renewed + row.ending_with_renewal + row.ending_without_renewal

            return row
        },

        getValueFromRow(rows, month, value) {
            let row = rows.data.data.find(row => row.month === month.format('YYYY-MM'))

            return row ? row[value] : 0
        }
    }
}
</script>
